import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Motorcycle, MapPin, Phone, Package, Check, ArrowsClockwise,
    SignOut, Clock, House, Printer,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAdmin } from "@/context/AdminContext";
import { api, authHeaders } from "@/lib/api";
import { subscribePush, pushSupported } from "@/lib/push";
import { smartPrint } from "@/lib/smartPrint";

const POLL_MS = 8_000;

/**
 * Driver / repartidor self-service screen. Reachable at /driver after the
 * staff signs in via /admin/login. Shows only orders assigned to them and
 * offers two big touch-friendly actions: "Recogí" → "Entregado".
 */
export default function DriverApp() {
    const { token, user, logout } = useAdmin();
    const nav = useNavigate();
    const [data, setData] = useState({ active: [], history: [], driver_name: "" });
    const [busy, setBusy] = useState(null);
    const [tab, setTab] = useState("active");

    const load = useCallback(async () => {
        try {
            const r = await api.get("/driver/orders", { headers: authHeaders() });
            setData(r.data);
        } catch (e) {
            if (e?.response?.status === 403) {
                toast.error("Tu usuario no tiene módulo Delivery");
                nav("/admin/login");
            }
        }
    }, [nav]);

    useEffect(() => {
        if (!token) return;
        load();
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [token, load]);

    // Once we know the driver's id, subscribe to push for new-assignment alerts.
    useEffect(() => {
        if (!data.driver_id || !pushSupported()) return;
        subscribePush({ driverUserId: data.driver_id }).catch(() => {});
    }, [data.driver_id]);

    if (!token || !user) return <Navigate to="/admin/login?next=/driver" replace />;

    const markPickedUp = async (order) => {
        setBusy(order.id);
        try {
            await api.put(`/driver/orders/${order.id}/picked-up`, {}, { headers: authHeaders() });
            toast.success("Marcado como recogido");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(null); }
    };

    const markDelivered = async (order) => {
        if (!window.confirm(`¿Confirmar entrega a ${order.customer_name}?`)) return;
        setBusy(order.id);
        try {
            await api.put(`/driver/orders/${order.id}/delivered`, {}, { headers: authHeaders() });
            toast.success("Entrega confirmada");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(null); }
    };

    return (
        <div className="min-h-screen bg-muted pb-20" data-testid="driver-app">
            {/* Top bar */}
            <header className="sticky top-0 z-20 bg-accent text-accent-foreground shadow-md">
                <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="grid place-items-center h-10 w-10 rounded-full bg-primary text-primary-foreground">
                            <Motorcycle size={20} weight="bold" />
                        </span>
                        <div>
                            <p className="text-[10px] uppercase tracking-widest opacity-70">
                                Repartidor
                            </p>
                            <p className="font-display font-black leading-none">
                                {data.driver_name || user.name || user.email}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="driver-refresh"
                            onClick={load}
                            className="h-9 w-9 grid place-items-center rounded-full bg-white/10"
                            title="Refrescar"
                        >
                            <ArrowsClockwise size={16} weight="bold" />
                        </button>
                        <button
                            data-testid="driver-signout"
                            onClick={() => { logout(); nav("/admin/login"); }}
                            className="h-9 w-9 grid place-items-center rounded-full bg-white/10"
                            title="Salir"
                        >
                            <SignOut size={16} weight="bold" />
                        </button>
                    </div>
                </div>
                <div className="flex border-t border-white/10">
                    {[
                        { id: "active", label: `Activos (${data.active.length})` },
                        { id: "history", label: `Hoy (${data.history.length})` },
                    ].map((t) => (
                        <button
                            key={t.id}
                            data-testid={`driver-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`flex-1 py-3 text-xs font-bold uppercase tracking-widest ${
                                tab === t.id
                                    ? "border-b-2 border-primary text-white"
                                    : "text-white/60"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="px-4 pt-4 space-y-3 max-w-2xl mx-auto">
                {tab === "active" ? (
                    data.active.length === 0 ? (
                        <EmptyState
                            icon={Motorcycle}
                            title="Sin entregas asignadas"
                            body="Espera a que el panel de cocina te asigne un pedido."
                        />
                    ) : (
                        data.active.map((o) => (
                            <DriverOrderCard
                                key={o.id}
                                order={o}
                                busy={busy === o.id}
                                onPickedUp={() => markPickedUp(o)}
                                onDelivered={() => markDelivered(o)}
                            />
                        ))
                    )
                ) : data.history.length === 0 ? (
                    <EmptyState icon={Package} title="Aún no has entregado nada hoy" body="" />
                ) : (
                    data.history.map((o) => (
                        <HistoryCard key={o.id} order={o} />
                    ))
                )}
            </main>
        </div>
    );
}

function DriverOrderCard({ order, busy, onPickedUp, onDelivered }) {
    const idShort = (order.id || "").slice(-6).toUpperCase();
    const pickedUp = !!order.picked_up_at;
    const phone = order.customer_phone ? `tel:${order.customer_phone.replace(/[^+0-9]/g, "")}` : null;
    const maps = order.address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.address)}`
        : null;
    return (
        <article
            data-testid={`driver-order-${order.id}`}
            className="bg-white rounded-3xl shadow-sm border border-border overflow-hidden"
        >
            <div className="px-5 py-4 bg-foreground text-background">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] opacity-70">
                            Pedido #{idShort}
                        </p>
                        <p className="font-display font-black text-2xl mt-0.5">
                            {order.customer_name}
                        </p>
                    </div>
                    <span className="text-sm font-bold uppercase tracking-widest bg-white/15 rounded-full px-3 py-1">
                        ${Number(order.total).toFixed(2)}
                    </span>
                </div>
                {order.eta_minutes && (
                    <p className="mt-2 text-xs inline-flex items-center gap-1 opacity-80">
                        <Clock size={12} weight="bold" /> ETA · {order.eta_minutes} min
                    </p>
                )}
            </div>

            <div className="px-5 py-4 space-y-3">
                {order.address && (
                    <a
                        href={maps}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`driver-map-${order.id}`}
                        className="flex items-start gap-2 rounded-2xl bg-muted p-3 hover:bg-muted/70"
                    >
                        <MapPin size={20} weight="fill" className="text-primary mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                Dirección
                            </p>
                            <p className="text-sm font-bold">{order.address}</p>
                        </div>
                        <House size={16} weight="bold" className="text-muted-foreground" />
                    </a>
                )}

                {order.customer_phone && (
                    <a
                        href={phone}
                        data-testid={`driver-call-${order.id}`}
                        className="flex items-center gap-2 rounded-2xl bg-muted p-3 hover:bg-muted/70"
                    >
                        <Phone size={20} weight="fill" className="text-primary shrink-0" />
                        <div className="flex-1">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                Teléfono
                            </p>
                            <p className="text-sm font-bold">{order.customer_phone}</p>
                        </div>
                    </a>
                )}

                <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">
                        Pedido
                    </p>
                    <ul className="space-y-1 text-sm">
                        {(order.items || []).map((it, i) => (
                            <li key={i} className="flex justify-between">
                                <span>
                                    <span className="font-bold text-primary mr-2">{it.qty}×</span>
                                    {it.name}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
                {order.notes && (
                    <p className="text-xs text-muted-foreground italic bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                        Nota: {order.notes}
                    </p>
                )}
            </div>

            <div className="px-5 pb-5 space-y-2">
                {!pickedUp ? (
                    <Button
                        data-testid={`driver-pickup-${order.id}`}
                        onClick={onPickedUp}
                        disabled={busy}
                        className="w-full h-14 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-widest text-base"
                    >
                        <Package size={20} weight="bold" className="mr-2" />
                        {busy ? "..." : "Recogí el pedido"}
                    </Button>
                ) : (
                    <Button
                        data-testid={`driver-deliver-${order.id}`}
                        onClick={onDelivered}
                        disabled={busy}
                        className="w-full h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-widest text-base"
                    >
                        <Check size={22} weight="bold" className="mr-2" />
                        {busy ? "..." : "Marcar como entregado"}
                    </Button>
                )}
                {pickedUp && (
                    <p className="text-center text-[11px] uppercase tracking-widest font-bold text-amber-700">
                        Recogido · en camino al cliente
                    </p>
                )}
                <button
                    data-testid={`driver-print-${order.id}`}
                    onClick={() => smartPrint(order, { kind: "customer" })}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-white border border-border py-3 text-sm font-bold uppercase tracking-widest hover:bg-muted"
                >
                    <Printer size={16} weight="bold" />
                    Imprimir recibo
                </button>
            </div>
        </article>
    );
}

function HistoryCard({ order }) {
    const idShort = (order.id || "").slice(-6).toUpperCase();
    return (
        <article className="bg-white rounded-2xl border border-border px-4 py-3 flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-emerald-100 text-emerald-700">
                <Check size={18} weight="bold" />
            </span>
            <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{order.customer_name}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                    #{idShort} · ${Number(order.total).toFixed(2)}
                </p>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {order.delivered_at ? new Date(order.delivered_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
            </span>
        </article>
    );
}

function EmptyState({ icon: Icon, title, body }) {
    return (
        <div className="bg-white rounded-3xl border border-border p-10 text-center" data-testid="driver-empty">
            <Icon size={48} weight="duotone" className="mx-auto opacity-40 mb-3" />
            <h2 className="font-display font-black text-xl">{title}</h2>
            {body && <p className="text-sm text-muted-foreground mt-1">{body}</p>}
        </div>
    );
}
